import { Ticket } from '@ads/icons';
import { Header } from '@linka/ui/header';
import { UserMenuDropdown } from '@linka/ui/user-menu-dropdown';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate, useNavigation } from 'react-router';
import { changeLanguage } from '@/app/i18n';
import { cn } from '@/lib/utils';
import { useSession } from '../hooks/use-session';

export interface HeaderLayoutProps {
  fixedHeader?: boolean;
}

interface DefaultLayoutProps extends HeaderLayoutProps {
  fullWidth?: boolean;
}

const DefaultLayout = ({ fixedHeader, fullWidth }: DefaultLayoutProps) => {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const loading = navigation.state === 'loading';

  return (
    <>
      <HeaderLayout fixedHeader={fixedHeader} />
      <main
        className={cn('px-4 py-6 max-w-[78rem] w-full mx-auto', {
          'px-0 py-0 max-w-full': fullWidth,
        })}
      >
        {loading ? (
          <p className='flex justify-center items-center h-full'>{t('loading')}</p>
        ) : (
          <div
            className={cn('', {
              'relative top-xxl': fixedHeader,
            })}
          >
            <Outlet />
          </div>
        )}
      </main>
    </>
  );
};

export const HeaderLayout = ({ fixedHeader }: HeaderLayoutProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const showAreaTitle = pathname !== '/' && !pathname.includes('contact');

  return (
    <Header
      variant='brand'
      logoHref='/events'
      renderLogoLink={({ href, children, ...props }) => (
        <Link to={href} {...props}>
          {children}
        </Link>
      )}
      areaTitle={showAreaTitle ? t('header.title') : undefined}
      supportLabel={t('need-help')}
      onSupportClick={() =>
        window.open('https://tawk.to/chat/67d027d60f8b4e190ad1487d/1imn7ffm3', '_blank', 'width=400,height=500')
      }
      className={cn('border-b border-ctx-layout-border', {
        'fixed top-0 left-0 w-full z-50': fixedHeader,
      })}
      menu={<UserActions />}
    />
  );
};

export const UserActions = () => {
  const { session, userData, signOut } = useSession();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const user = session.data ? { name: userData?.name ?? '' } : null;
  const showAccessAccount = pathname !== '/auth/login';

  return (
    <nav className='flex items-center gap-mini'>
      <UserMenuDropdown
        menuAriaLabel={t('open-menu')}
        isPending={session.isPending}
        user={user}
        items={[
          {
            key: 'my-events',
            label: t('my-events'),
            icon: () => <Ticket />,
            onClick: () => nav('/events'),
          },
        ]}
        signOutLabel={t('exit')}
        onSignOut={() =>
          signOut({
            fetchOptions: {
              onSuccess: () => {
                window.location.href = '/auth/login';
              },
            },
          })
        }
        accessAccountLabel={showAccessAccount ? t('access-your-account') : undefined}
        onAccessAccount={() => nav('/auth/login')}
        registerLabel={!session.data ? t('register') : undefined}
        onRegister={() => nav('/auth/register')}
        language={language}
        onLanguageChange={(lang) => changeLanguage(lang)}
        languageTitle={t('language')}
      />
    </nav>
  );
};

export default DefaultLayout;
